import { WeatherCondition, WeatherDay, WeatherDayKind } from '@models/weather.model';

/**
 * Sprint 25, story-133. Schedule-local weather copy/formatting — mirrors
 * `subcontractors/utils/format.ts`'s repo convention (`useTranslation` returns raw
 * `{placeholder}` template strings, substituted with a plain `.replace`, no i18n library).
 */

// Mirrors `backend/src/weather/weather-condition.constants.ts`'s `WORKING_HOURS`. Duplicated
// deliberately, not fetched — the GraphQL schema doesn't expose the window as a field (the AC
// only asks for the stored rows), so this display-only constant is the single place to update if
// the backend's own constant ever changes.
export const WEATHER_WORKING_HOURS_DISPLAY = { start: '07:00', end: '17:00' };

export interface WeatherLocaleStrings {
  weatherForecast: string;
  weatherObserved: string;
  weatherObservedSoFar: string;
  weatherWorstWindow: string;
  weatherHigh: string;
  weatherLow: string;
  weatherPrecip: string;
  weatherPrecipChance: string;
  weatherSource: string;
  weatherSourceStation: string;
  weatherLegend: string;
  weatherNoLocation: string;
  weatherConditionThunderstorm: string;
  weatherConditionIce: string;
  weatherConditionSnow: string;
  weatherConditionHeavyRain: string;
  weatherConditionRain: string;
  weatherConditionDrizzle: string;
  weatherConditionFog: string;
  weatherConditionWind: string;
  weatherConditionCloudy: string;
  weatherConditionPartlyCloudy: string;
  weatherConditionClear: string;
}

const CONDITION_LABEL_KEYS: Record<WeatherCondition, keyof WeatherLocaleStrings> = {
  THUNDERSTORM: 'weatherConditionThunderstorm',
  ICE: 'weatherConditionIce',
  SNOW: 'weatherConditionSnow',
  HEAVY_RAIN: 'weatherConditionHeavyRain',
  RAIN: 'weatherConditionRain',
  DRIZZLE: 'weatherConditionDrizzle',
  FOG: 'weatherConditionFog',
  WIND: 'weatherConditionWind',
  CLOUDY: 'weatherConditionCloudy',
  PARTLY_CLOUDY: 'weatherConditionPartlyCloudy',
  CLEAR: 'weatherConditionClear',
};

const fill = (template: string, params: Record<string, string | number>): string =>
  Object.entries(params).reduce((acc, [key, value]) => acc.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value)), template);

export const conditionLabel = (condition: WeatherCondition, strings: WeatherLocaleStrings): string =>
  strings[CONDITION_LABEL_KEYS[condition]] ?? condition;

const kindLabel = (kind: WeatherDayKind, isToday: boolean, strings: WeatherLocaleStrings): string => {
  if (kind === 'FORECAST') return strings.weatherForecast;
  if (isToday) return strings.weatherObservedSoFar;
  return strings.weatherObserved;
};

const formatTemp = (value: number | null): string | null => (value == null ? null : `${Math.round(value)}°`);

/**
 * Builds the tooltip's plain-text content (native `title`/`aria-label`, per the UX doc's
 * documented fallback for the custom-popover interaction pattern). `\n` renders as a line break
 * in a native browser title tooltip.
 */
export const buildWeatherTooltipText = (
  day: WeatherDay,
  strings: WeatherLocaleStrings,
  dateLocale: string,
  isToday: boolean,
): string => {
  const lines: string[] = [];

  const dateLabel = new Intl.DateTimeFormat(dateLocale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(new Date(`${day.date}T00:00:00`));
  lines.push(`${dateLabel} · ${kindLabel(day.kind, isToday, strings)}`);

  if (!day.condition) return lines.join('\n');

  const label = conditionLabel(day.condition, strings);
  if (day.kind === 'FORECAST') {
    lines.push(label);
  } else {
    lines.push(
      `${label} — ${fill(strings.weatherWorstWindow, {
        start: WEATHER_WORKING_HOURS_DISPLAY.start,
        end: WEATHER_WORKING_HOURS_DISPLAY.end,
      })}`,
    );
  }

  const high = formatTemp(day.highTemp);
  const low = formatTemp(day.lowTemp);
  const tempParts: string[] = [];
  if (high != null) tempParts.push(`${strings.weatherHigh} ${high}`);
  if (low != null) tempParts.push(`${strings.weatherLow} ${low}`);
  if (day.kind === 'FORECAST' && day.precipChance != null) {
    tempParts.push(fill(strings.weatherPrecipChance, { pct: Math.round(day.precipChance) }));
  } else if (day.precipitation != null) {
    tempParts.push(`${strings.weatherPrecip} ${day.precipitation} in`);
  }
  if (tempParts.length) lines.push(tempParts.join(' · '));

  if (day.kind === 'OBSERVED') {
    lines.push(day.station ? fill(strings.weatherSourceStation, { station: day.station }) : strings.weatherSource);
  }

  return lines.join('\n');
};

export const buildWeatherLegendText = (strings: WeatherLocaleStrings): string =>
  fill(strings.weatherLegend, {
    start: WEATHER_WORKING_HOURS_DISPLAY.start,
    end: WEATHER_WORKING_HOURS_DISPLAY.end,
  });
